import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Search, Send, Sparkles, Plus, Loader2, BookOpen, Download, FileText,
  PanelLeft, PanelRight, Copy, Volume2, Flag, Scale, History as HistoryIcon,
  Folder, FolderPlus, MoreHorizontal, Star, Undo2, GitCompare, Zap, Trash2, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useServerFn } from "@tanstack/react-start";
import { searchLaws, exportArticlePdf, exportArticleBriefPdf } from "@/lib/laws.functions";
import {
  listFolders, createFolder, renameFolder, deleteFolder, moveSessionToFolder,
} from "@/lib/research-folders.functions";
import {
  listSavedPrompts, savePrompt, deleteSavedPrompt, undoLastRevision,
} from "@/lib/saved-prompts.functions";
import { exportAllSources } from "@/lib/research-export.functions";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { QuotaExceededDialog } from "@/components/app/quota-exceeded-dialog";
import { AiMarkdown } from "@/components/app/ai-markdown";
import { useTranslation } from "react-i18next";
import { openWhatsAppContact } from "@/lib/whatsapp-contact";


const ResearchSearchSchema = z.object({
  q: z.string().optional(),
  mode: z.enum(["compare"]).optional(),
});

export const Route = createFileRoute("/_authenticated/app/research")({
  validateSearch: (s) => ResearchSearchSchema.parse(s),
  component: ResearchPage,
});

type Msg = { id?: string; role: "user" | "assistant"; content: string };
type Session = { id: string; title: string; updated_at: string; kind?: string | null; folder_id?: string | null };
type Folder = { id: string; name: string; parent_id: string | null };
type SavedPrompt = { id: string; label: string; prompt: string; created_at: string };

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/legal-research`;

type Citation = {
  id: string;
  article_number: string;
  title: string | null;
  body: string;
  legal_sources: { short_name: string; full_title: string } | null;
};

const SYSTEM_HINT = `\n\n[ISTRUZIONI DI CITAZIONE]\nQuando usi una fonte dell'elenco recuperato, inserisci nel testo un riferimento nel formato esatto [CIT. N] dove N è l'indice (1-based) della fonte. Non inventare riferimenti. Se nessuna fonte è pertinente, non citare nulla.`;

function ResearchPage() {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [filterKind, setFilterKind] = useState<"all" | "ask" | "draft" | "compare">("all");
  const [sessionSearch, setSessionSearch] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [citations, setCitations] = useState<Citation[]>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [briefId, setBriefId] = useState<string | null>(null);
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [rightTab, setRightTab] = useState<"sources" | "sentences" | "revisions">("sources");
  const [revisionPrompt, setRevisionPrompt] = useState("");
  const [revisionsUsed, setRevisionsUsed] = useState(0);
  const [revisionsLimit] = useState(10);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const [savedPrompts, setSavedPrompts] = useState<SavedPrompt[]>([]);
  const [exportingZip, setExportingZip] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [quotaOpen, setQuotaOpen] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState<{ used?: number; limit?: number }>({});
  const [dragSessionId, setDragSessionId] = useState<string | null>(null);
  const [dragOverFolder, setDragOverFolder] = useState<string | "root" | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sourceRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const searchLawsFn = useServerFn(searchLaws);
  const exportArticlePdfFn = useServerFn(exportArticlePdf);
  const exportBriefFn = useServerFn(exportArticleBriefPdf);
  const listFoldersFn = useServerFn(listFolders);
  const createFolderFn = useServerFn(createFolder);
  const renameFolderFn = useServerFn(renameFolder);
  const deleteFolderFn = useServerFn(deleteFolder);
  const moveSessionFn = useServerFn(moveSessionToFolder);
  const listPromptsFn = useServerFn(listSavedPrompts);
  const savePromptFn = useServerFn(savePrompt);
  const deletePromptFn = useServerFn(deleteSavedPrompt);
  const undoFn = useServerFn(undoLastRevision);
  const exportZipFn = useServerFn(exportAllSources);

  const loadSessions = async () => {
    const { data } = await supabase
      .from("research_sessions")
      .select("id, title, updated_at, kind, folder_id")
      .order("updated_at", { ascending: false });
    setSessions((data ?? []) as Session[]);
  };

  const loadFolders = async () => {
    try { const r: any = await listFoldersFn(); if (r.ok) setFolders(r.folders); } catch {}
  };
  const loadPrompts = async () => {
    try { const r: any = await listPromptsFn(); if (r.ok) setSavedPrompts(r.prompts); } catch {}
  };

  useEffect(() => { loadSessions(); loadFolders(); loadPrompts(); }, []);

  useEffect(() => {
    if (!activeId) { setMessages([]); return; }
    supabase
      .from("research_messages")
      .select("id, role, content")
      .eq("session_id", activeId)
      .order("created_at", { ascending: true })
      .then(({ data }) => setMessages((data as Msg[]) ?? []));
    supabase
      .from("research_sessions")
      .select("revisions_used")
      .eq("id", activeId)
      .single()
      .then(({ data }) => setRevisionsUsed((data as any)?.revisions_used ?? 0));
  }, [activeId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const newSession = async (kind: "ask" | "draft" | "compare" = "ask") => {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;
    const { data, error } = await supabase
      .from("research_sessions")
      .insert({ owner_id: u.user.id, title: t("pages.research.newSessionTitle"), kind } as any)
      .select()
      .single();
    if (error) { toast.error(error.message); return; }
    setActiveId(data.id);
    setMessages([]);
    setCitations([]);
    loadSessions();
  };

  const triggerDownload = (r: { ok: boolean; base64?: string; mimeType?: string; filename?: string; error?: string }) => {
    if (!r.ok || !r.base64) throw new Error(r.error || t("pages.common.error"));
    const bin = atob(r.base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const blob = new Blob([arr], { type: r.mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = r.filename || "documento.pdf"; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadArticle = async (c: Citation) => {
    setDownloadingId(c.id);
    try { triggerDownload((await exportArticlePdfFn({ data: { articleId: c.id } })) as any); }
    catch (e: any) { toast.error(e?.message ?? t("pages.research.downloadFailed")); }
    finally { setDownloadingId(null); }
  };

  const downloadBrief = async (c: Citation) => {
    setBriefId(c.id);
    try { triggerDownload((await exportBriefFn({ data: { articleId: c.id } })) as any); toast.success(t("pages.research.briefGenerated")); }
    catch (e: any) { toast.error(e?.message ?? t("pages.research.generateFailed")); }
    finally { setBriefId(null); }
  };

  const downloadAllSources = async () => {
    if (!citations.length || !activeId) return;
    setExportingZip(true);
    toast.info(t("pages.research.preparingZip", { n: citations.length }));
    try {
      const r: any = await exportZipFn({ data: { sessionId: activeId, articleIds: citations.map((c) => c.id) } });
      if (!r.ok) throw new Error(r.error);
      triggerDownload(r);
      toast.success(t("pages.research.zipReady", { kb: (r.size / 1024).toFixed(1) }));
    } catch (e: any) {
      toast.error(e?.message ?? t("pages.research.exportFailed"));
    } finally {
      setExportingZip(false);
    }
  };

  const handleUndoRevision = async () => {
    if (!activeId) return;
    setUndoing(true);
    try {
      const r: any = await undoFn({ data: { sessionId: activeId } });
      if (!r.ok) throw new Error(r.error);
      setRevisionsUsed(r.revisionsUsed);
      // reload messages
      const { data } = await supabase.from("research_messages")
        .select("id, role, content").eq("session_id", activeId)
        .order("created_at", { ascending: true });
      setMessages((data as Msg[]) ?? []);
      toast.success(t("pages.research.revisionUndone"));
    } catch (e: any) {
      toast.error(e?.message ?? t("pages.common.error"));
    } finally {
      setUndoing(false);
    }
  };

  const handleSavePrompt = async () => {
    const text = (input || revisionPrompt).trim();
    if (!text) { toast.error(t("pages.research.writePromptFirst")); return; }
    const label = text.slice(0, 60);
    try {
      const r: any = await savePromptFn({ data: { label, prompt: text } });
      if (!r.ok) throw new Error(r.error);
      toast.success(t("pages.research.promptSaved"));
      loadPrompts();
    } catch (e: any) { toast.error(e?.message ?? t("pages.common.error")); }
  };

  const handleDeletePrompt = async (id: string) => {
    try { await deletePromptFn({ data: { id } }); loadPrompts(); } catch {}
  };

  const handleNewFolder = async () => {
    const name = window.prompt(t("pages.research.folderNamePrompt"));
    if (!name) return;
    const r: any = await createFolderFn({ data: { name } });
    if (r.ok) { loadFolders(); toast.success(t("pages.research.folderCreated")); }
    else toast.error(r.error);
  };

  const handleRenameFolder = async (f: Folder) => {
    const name = window.prompt(t("pages.research.folderRenamePrompt"), f.name);
    if (!name || name === f.name) return;
    const r: any = await renameFolderFn({ data: { id: f.id, name } });
    if (r.ok) loadFolders();
  };

  const handleDeleteFolder = async (f: Folder) => {
    if (!window.confirm(t("pages.research.folderDeleteConfirm", { name: f.name }))) return;
    const r: any = await deleteFolderFn({ data: { id: f.id } });
    if (r.ok) { loadFolders(); loadSessions(); toast.success(t("pages.research.folderDeleted")); }
  };

  const handleMoveSession = async (sessionId: string, folderId: string | null) => {
    const r: any = await moveSessionFn({ data: { sessionId, folderId } });
    if (r.ok) loadSessions();
  };

  const scrollToCitation = (n: number) => {
    setRightOpen(true);
    setRightTab("sources");
    setTimeout(() => {
      const el = sourceRefs.current[n - 1];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-emerald-400/60");
        setTimeout(() => el.classList.remove("ring-2", "ring-emerald-400/60"), 1500);
      }
    }, 80);
  };

  const speak = (text: string) => {
    if (!("speechSynthesis" in window)) return toast.error(t("pages.research.ttsUnavailable"));
    const u = new SpeechSynthesisUtterance(text.replace(/\[CIT\.\s*\d+\]/g, ""));
    u.lang = "it-IT";
    speechSynthesis.cancel();
    speechSynthesis.speak(u);
  };

  const send = async (overrideMessage?: string) => {
    const queryText = (overrideMessage ?? input).trim();
    if (!queryText || loading) return;
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;

    let sessionId = activeId;
    if (!sessionId) {
      const { data, error } = await supabase
        .from("research_sessions")
        .insert({ owner_id: u.user.id, title: queryText.slice(0, 60), kind: "ask" } as any)
        .select().single();
      if (error) { toast.error(error.message); return; }
      sessionId = data.id;
      setActiveId(sessionId);
      loadSessions();
    }

    const userMsg: Msg = { role: "user", content: queryText };
    setMessages((p) => [...p, userMsg, { role: "assistant", content: "" }]);
    if (!overrideMessage) setInput("");
    setLoading(true);

    await supabase.from("research_messages").insert({
      owner_id: u.user.id, session_id: sessionId, role: "user", content: userMsg.content,
    });

    setCitations([]);
    let retrieved: Citation[] = [];
    try {
      const r = await searchLawsFn({ data: { query: queryText, limit: 8 } });
      if (r.ok) { retrieved = (r.results ?? []) as Citation[]; setCitations(retrieved); }
    } catch {}

    const contextBlock = retrieved.length
      ? `\n\n[CONTESTO NORMATIVO RECUPERATO - usa SOLO queste fonti se pertinenti]\n` +
        retrieved.map((c, i) =>
          `[${i + 1}] art. ${c.article_number}${c.legal_sources?.short_name ? ` ${c.legal_sources.short_name}` : ""}${c.title ? ` (${c.title})` : ""}: ${c.body.slice(0, 1200)}`
        ).join("\n\n") + SYSTEM_HINT
      : "";

    const augmentedHistory: Msg[] = [
      ...messages,
      { role: "user", content: queryText + contextBlock },
    ];

    let assistantText = "";
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ messages: augmentedHistory }),
      });
      if (resp.status === 429) { toast.error(t("pages.research.requestLimit")); setLoading(false); return; }
      if (resp.status === 402) {
        let info: any = {};
        try { info = await resp.json(); } catch {}
        setQuotaInfo({ used: info?.used, limit: info?.limit });
        setQuotaOpen(true);
        setMessages((p) => p.slice(0, -2));
        setLoading(false);
        return;
      }
      if (!resp.ok || !resp.body) throw new Error(t("pages.research.streamError"));

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let done = false;
      while (!done) {
        const { done: d, value } = await reader.read();
        if (d) break;
        buffer += decoder.decode(value, { stream: true });
        let idx: number;
        while ((idx = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6).trim();
          if (json === "[DONE]") { done = true; break; }
          try {
            const parsed = JSON.parse(json);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantText += content;
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = { role: "assistant", content: assistantText };
                return next;
              });
            }
          } catch { buffer = line + "\n" + buffer; break; }
        }
      }

      await supabase.from("research_messages").insert({
        owner_id: u.user.id, session_id: sessionId, role: "assistant", content: assistantText,
      });
    } catch (e) {
      console.error(e);
      toast.error(t("pages.research.requestError"));
    } finally {
      setLoading(false);
    }
  };

  const sendRevision = async () => {
    if (!revisionPrompt.trim() || !activeId) return;
    if (revisionsUsed >= revisionsLimit) { toast.error(t("pages.research.revisionLimit")); return; }
    const prompt = `Rivedi la risposta precedente con questa indicazione: ${revisionPrompt.trim()}`;
    setRevisionPrompt("");
    await supabase.from("research_sessions").update({ revisions_used: revisionsUsed + 1 } as any).eq("id", activeId);
    setRevisionsUsed((n) => n + 1);
    await send(prompt);
  };

  const filteredSessions = useMemo(() => {
    return sessions.filter((s) =>
      (filterKind === "all" || (s.kind ?? "ask") === filterKind) &&
      (activeFolderId === null || s.folder_id === activeFolderId) &&
      (sessionSearch === "" || s.title.toLowerCase().includes(sessionSearch.toLowerCase()))
    );
  }, [sessions, filterKind, sessionSearch, activeFolderId]);

  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant")?.content ?? "";

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-background text-foreground">
      <QuotaExceededDialog open={quotaOpen} onOpenChange={setQuotaOpen} used={quotaInfo.used} limit={quotaInfo.limit} />
      {leftOpen && (
        <aside className="hidden w-72 shrink-0 flex-col border-r border-border/60 bg-muted/20 p-3 lg:flex">
          <div className="flex items-center gap-2">
            <Button onClick={() => newSession("ask")} className="flex-1 justify-start gap-2" size="sm">
              <Plus className="h-4 w-4" /> {t("pages.research.newSearch")}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setLeftOpen(false)} aria-label={t("pages.common.close")}>
              <PanelLeft className="h-4 w-4" />
            </Button>
          </div>
          <div className="relative mt-3">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={sessionSearch} onChange={(e) => setSessionSearch(e.target.value)} placeholder={t("pages.research.filterPlaceholder")} className="h-8 pl-8 text-xs" />
          </div>
          <div className="mt-2 flex items-center gap-1 text-[11px]">
            {(["all", "ask", "draft", "compare"] as const).map((k) => (
              <button key={k} onClick={() => setFilterKind(k)}
                className={cn("rounded-full px-2 py-1 capitalize transition", filterKind === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>
                {k === "all" ? t("pages.research.filterAll") : k === "ask" ? t("pages.research.filterAsk") : k === "draft" ? t("pages.research.filterDraft") : t("pages.research.filterCompare")}
              </button>
            ))}
          </div>
          <div className="mt-3 border-t border-border/40 pt-2">
            <div className="mb-1 flex items-center justify-between px-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t("pages.research.folders")}</span>
              <button onClick={handleNewFolder} title={t("pages.research.newFolder")} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
                <FolderPlus className="h-3.5 w-3.5" />
              </button>
            </div>
            <button onClick={() => setActiveFolderId(null)}
              onDragOver={(e) => { if (dragSessionId) { e.preventDefault(); setDragOverFolder("root"); } }}
              onDragLeave={() => setDragOverFolder((p) => (p === "root" ? null : p))}
              onDrop={(e) => {
                e.preventDefault();
                if (dragSessionId) handleMoveSession(dragSessionId, null);
                setDragOverFolder(null); setDragSessionId(null);
              }}
              className={cn("flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-xs transition",
                activeFolderId === null ? "bg-accent/15 text-foreground" : "text-muted-foreground hover:bg-muted/40",
                dragOverFolder === "root" && "ring-2 ring-accent/60")}>
              <Folder className="h-3 w-3" /> {t("pages.research.filterAll")}
            </button>
            {folders.map((f) => (
              <div key={f.id}
                onDragOver={(e) => { if (dragSessionId) { e.preventDefault(); setDragOverFolder(f.id); } }}
                onDragLeave={() => setDragOverFolder((p) => (p === f.id ? null : p))}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragSessionId) handleMoveSession(dragSessionId, f.id);
                  setDragOverFolder(null); setDragSessionId(null);
                }}
                className={cn("group flex items-center gap-1 rounded-md px-2 py-1 text-xs transition",
                activeFolderId === f.id ? "bg-accent/15 text-foreground" : "text-muted-foreground hover:bg-muted/40",
                dragOverFolder === f.id && "ring-2 ring-accent/60")}>
                <button onClick={() => setActiveFolderId(f.id)} className="flex flex-1 items-center gap-2 truncate text-left">
                  <Folder className="h-3 w-3" /> <span className="truncate">{f.name}</span>
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="opacity-0 group-hover:opacity-100"><MoreHorizontal className="h-3 w-3" /></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleRenameFolder(f)}><Pencil className="mr-2 h-3 w-3" />Rinomina</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDeleteFolder(f)} className="text-destructive"><Trash2 className="mr-2 h-3 w-3" />Elimina</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
          <div className="mt-3 flex-1 space-y-1 overflow-y-auto">
            {filteredSessions.map((s) => (
              <div key={s.id}
                draggable
                onDragStart={(e) => { setDragSessionId(s.id); e.dataTransfer.effectAllowed = "move"; }}
                onDragEnd={() => { setDragSessionId(null); setDragOverFolder(null); }}
                className={cn("group flex items-center gap-1 rounded-md pr-1 transition-colors cursor-grab active:cursor-grabbing",
                  activeId === s.id ? "bg-accent/15 text-foreground" : "text-muted-foreground hover:bg-muted/40",
                  dragSessionId === s.id && "opacity-50")}>
                <button onClick={() => setActiveId(s.id)}
                  className="flex min-w-0 flex-1 items-center gap-2 px-2.5 py-2 text-left text-sm">
                  <KindDot kind={(s.kind ?? "ask") as any} />
                  <span className="min-w-0 flex-1 truncate" title={s.title}>{s.title}</span>
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="opacity-0 group-hover:opacity-100"><MoreHorizontal className="h-3 w-3" /></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleMoveSession(s.id, null)}>{t("pages.research.noFolder")}</DropdownMenuItem>
                    {folders.map((f) => (
                      <DropdownMenuItem key={f.id} onClick={() => handleMoveSession(s.id, f.id)}>
                        <Folder className="mr-2 h-3 w-3" />{f.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
            {filteredSessions.length === 0 && (
              <p className="px-2 py-3 text-xs text-muted-foreground">{t("pages.research.noSessions")}</p>
            )}
          </div>
        </aside>
      )}

      {/* CENTER */}
      <div className="flex flex-1 flex-col">
        <div className="flex items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {!leftOpen && (
              <Button variant="ghost" size="icon" onClick={() => setLeftOpen(true)} aria-label={t("pages.common.open")} className="shrink-0">
                <PanelLeft className="h-4 w-4" />
              </Button>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{t("pages.research.eyebrow")}</p>
              <h1
                className="truncate font-display text-lg font-semibold leading-tight"
                title={sessions.find((s) => s.id === activeId)?.title ?? t("pages.research.placeholder")}
              >
                {sessions.find((s) => s.id === activeId)?.title ?? t("pages.research.placeholder")}
              </h1>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5 text-xs">
            <Badge variant="outline" className="hidden gap-1.5 sm:inline-flex">
              <HistoryIcon className="h-3 w-3" /> {revisionsUsed}/{revisionsLimit}
            </Badge>
            {activeId && messages.length > 0 && (
              <Button variant="ghost" size="sm" onClick={handleUndoRevision} disabled={undoing} className="hidden h-7 gap-1 md:inline-flex">
                {undoing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />} {t("pages.research.undo")}
              </Button>
            )}
            <Button asChild variant="ghost" size="sm" className="hidden h-7 gap-1 lg:inline-flex">
              <Link to="/app/research/compare"><GitCompare className="h-3 w-3" /> {t("pages.research.compare")}</Link>
            </Button>
            <Button variant="ghost" size="sm" className="hidden h-7 gap-1 lg:inline-flex" onClick={() => openWhatsAppContact("Ciao Gianluca, vorrei aggiungere crediti AI al mio piano LexAI Pro.")}>
              <Zap className="h-3 w-3" /> {t("pages.research.credits")}
            </Button>

            <Button variant="ghost" size="icon" onClick={() => setRightOpen((v) => !v)} aria-label={t("pages.research.tabSources")} className="shrink-0">
              <PanelRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-6 sm:px-8">
          {messages.length === 0 ? (
            <EmptyResearch onPick={(q) => setInput(q)} />
          ) : (
            <div className="mx-auto max-w-3xl space-y-5">
              {messages.map((m, i) => (
                <article key={i} className={cn("rounded-xl border border-border/60 p-4",
                  m.role === "assistant" ? "bg-muted/20" : "bg-card")}>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                      {m.role === "user" ? t("pages.research.query") : "LexAIPro"}
                    </span>
                    {m.role === "assistant" && m.content && (
                      <div className="flex items-center gap-1 opacity-60 transition group-hover:opacity-100">
                        <button title={t("pages.research.copy")} onClick={() => { navigator.clipboard.writeText(m.content); toast.success(t("pages.research.copied")); }} className="rounded p-1 hover:bg-muted">
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <button title={t("pages.research.listen")} onClick={() => speak(m.content)} className="rounded p-1 hover:bg-muted">
                          <Volume2 className="h-3.5 w-3.5" />
                        </button>
                        <button title={t("pages.research.report")} onClick={() => toast.success(t("pages.research.reported"))} className="rounded p-1 hover:bg-muted">
                          <Flag className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="text-[15px] leading-relaxed text-foreground">
                    {m.role === "assistant" ? (
                      <AiMarkdown onCitationClick={scrollToCitation}>{m.content}</AiMarkdown>
                    ) : (
                      <div className="whitespace-pre-wrap">{m.content}</div>
                    )}
                    {loading && i === messages.length - 1 && m.role === "assistant" && (
                      <span className="ml-1 inline-block h-3 w-1 animate-pulse bg-emerald-400" />
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border-t border-border/60 bg-background/95 px-4 py-3 backdrop-blur">
          <div className="mx-auto flex max-w-3xl items-end gap-2">
            <Textarea value={input} onChange={(e) => setInput(e.target.value)} placeholder={t("pages.research.composerPlaceholder")}
              rows={2} disabled={loading}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
            <Button onClick={() => send()} disabled={loading || !input.trim()} size="lg">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <p className="mx-auto mt-2 max-w-3xl text-center text-[11px] text-muted-foreground">
            {t("pages.research.disclaimer")}
          </p>
        </div>
      </div>

      {/* RIGHT */}
      {rightOpen && (
        <aside className="hidden w-[340px] shrink-0 flex-col border-l border-border/60 bg-muted/10 lg:flex">
          <div className="flex items-center gap-1 border-b border-border/60 px-2 py-2">
            {([
              { id: "sources", label: t("pages.research.tabSources"), icon: BookOpen, count: citations.length },
              { id: "sentences", label: t("pages.research.tabSentences"), icon: Scale, count: 0 },
              { id: "revisions", label: t("pages.research.tabRevisions"), icon: HistoryIcon, count: revisionsUsed },
            ] as const).map((t) => (
              <button key={t.id} onClick={() => setRightTab(t.id)}
                className={cn("flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition",
                  rightTab === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>
                <t.icon className="h-3.5 w-3.5" />
                {t.label}
                {t.count > 0 && <span className="ml-0.5 rounded-full bg-emerald-500/15 px-1.5 text-[10px] font-bold text-emerald-300">{t.count}</span>}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {rightTab === "sources" && (
              <>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t("pages.research.libraryHeader", { n: citations.length })}</p>
                  {citations.length > 0 && (
                    <Button size="sm" variant="ghost" onClick={downloadAllSources} disabled={exportingZip} className="h-7 gap-1 text-[11px]">
                      {exportingZip ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} {t("pages.research.sourcesZip")}
                    </Button>
                  )}
                </div>
                {citations.length === 0 ? (
                  <p className="px-1 text-xs text-muted-foreground">{t("pages.research.sourcesEmpty")}</p>
                ) : (
                  <div className="space-y-2">
                    {citations.map((c, i) => (
                      <div key={c.id} ref={(el) => { sourceRefs.current[i] = el; }}
                        className="rounded-md border border-border/60 bg-background/40 p-2.5 transition">
                        <div className="flex items-center justify-between gap-2">
                          <Badge variant="secondary" className="font-mono text-[10px]">CIT. {i + 1} - NORMATIVA</Badge>
                          <div className="flex gap-1">
                            <button onClick={() => downloadArticle(c)} disabled={downloadingId === c.id}
                              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" title={t("pages.research.pdfArticle")}>
                              {downloadingId === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                            </button>
                            <button onClick={() => downloadBrief(c)} disabled={briefId === c.id}
                              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" title={t("pages.research.aiBrief")}>
                              {briefId === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
                            </button>
                          </div>
                        </div>
                        <p className="mt-1.5 text-xs font-medium">
                          art. {c.article_number}{c.legal_sources?.short_name ? ` ${c.legal_sources.short_name}` : ""}
                          {c.title ? ` - ${c.title}` : ""}
                        </p>
                        <p className="mt-1 line-clamp-3 text-[11px] leading-relaxed text-muted-foreground">{c.body}</p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {rightTab === "sentences" && (
              <div className="space-y-3 text-xs">
                <div className="flex gap-1">
                  {[t("pages.research.sentenceFilters.all"), t("pages.research.sentenceFilters.conformi"), t("pages.research.sentenceFilters.difformi")].map((l) => (
                    <span key={l} className="rounded-full border border-border/60 px-2 py-1 text-[10px] text-muted-foreground">{l} (0)</span>
                  ))}
                </div>
                <p className="text-muted-foreground">{t("pages.research.sentencesHint")}</p>
              </div>
            )}

            {rightTab === "revisions" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t("pages.research.revisions")}</p>
                  <span className="text-[11px] text-muted-foreground">{revisionsUsed}/{revisionsLimit}</span>
                </div>
                <Textarea value={revisionPrompt} onChange={(e) => setRevisionPrompt(e.target.value)} rows={3}
                  placeholder={t("pages.research.revisionPlaceholder")} disabled={!lastAssistant || revisionsUsed >= revisionsLimit} />
                <Button size="sm" onClick={sendRevision} disabled={!revisionPrompt.trim() || !lastAssistant || revisionsUsed >= revisionsLimit} className="w-full">
                  {t("pages.research.applyRevision")}
                </Button>
                {revisionsUsed >= revisionsLimit && (
                  <p className="text-[11px] text-amber-400">{t("pages.research.noRevisionsLeft")}</p>
                )}
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={handleSavePrompt} className="flex-1 gap-1">
                    <Star className="h-3 w-3" /> {t("pages.research.savePrompt")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleUndoRevision} disabled={undoing || !messages.length} className="flex-1 gap-1">
                    {undoing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />} {t("pages.research.undo")}
                  </Button>
                </div>
                <div className="border-t border-border/40 pt-3">
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t("pages.research.savedPrompts", { n: savedPrompts.length })}</p>
                  <div className="space-y-1">
                    {savedPrompts.length === 0 && (
                      <p className="text-[11px] text-muted-foreground">{t("pages.research.noSavedPrompts")}</p>
                    )}
                    {savedPrompts.map((p) => (
                      <div key={p.id} className="group flex items-center gap-1 rounded border border-border/60 bg-background/40 p-2">
                        <button onClick={() => setRevisionPrompt(p.prompt)} className="flex-1 truncate text-left text-[11px]" title={p.prompt}>
                          {p.label}
                        </button>
                        <button onClick={() => handleDeletePrompt(p.id)} className="opacity-0 transition group-hover:opacity-100">
                          <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </aside>
      )}
    </div>
  );
}

function KindDot({ kind }: { kind: "ask" | "draft" | "compare" }) {
  const color = kind === "ask" ? "bg-rose-400" : kind === "draft" ? "bg-sky-400" : "bg-emerald-400";
  return <span className={cn("inline-block h-1.5 w-1.5 shrink-0 rounded-full", color)} />;
}

function renderWithCitations(text: string, onClick: (n: number) => void) {
  const parts: (string | { n: number })[] = [];
  const re = /\[CIT\.\s*(\d+)\]/g;
  let last = 0; let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push({ n: parseInt(m[1], 10) });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.map((p, i) =>
    typeof p === "string"
      ? <span key={i}>{p}</span>
      : (
        <button key={i} type="button" onClick={() => onClick(p.n)}
          className="mx-0.5 inline-flex items-center gap-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 align-middle text-[10px] font-semibold text-emerald-300 transition hover:bg-emerald-500/20">
          CIT. {p.n}
        </button>
      )
  );
}

function EmptyResearch({ onPick }: { onPick: (q: string) => void }) {
  const { t } = useTranslation();
  const suggestions = (t("pages.research.suggestions", { returnObjects: true }) as string[]) ?? [];
  return (
    <div className="mx-auto max-w-2xl pt-12 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-300">
        <Sparkles className="h-6 w-6" />
      </div>
      <h2 className="font-display text-3xl font-semibold">{t("pages.research.emptyTitle")}</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        {t("pages.research.emptyDesc")}
      </p>
      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        <Badge variant="outline" className="gap-1.5 text-[11px]"><Sparkles className="h-3 w-3" /> {t("pages.research.verifiedCitations")}</Badge>
        <Badge variant="outline" className="gap-1.5 text-[11px]"><BookOpen className="h-3 w-3" /> {t("pages.research.officialSources")}</Badge>
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
        {suggestions.map((q) => (
          <button key={q} type="button" onClick={() => onPick(q)}
            className="rounded-full border border-border/60 bg-card px-3 py-1.5 text-xs text-muted-foreground transition hover:border-foreground/40 hover:text-foreground">
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

